<template>
  <div class="content-box cr-pantalla">
    <!-- Arriba para que siempre se vea, aunque la lista de productos crezca -->
    <!-- Mismo estilo que "Ver todos los Requerimientos" -->
    <router-link class="btn btn-sm btn-info d-inline-flex align-items-center cr-btn-listado" :to="rutaListado">
      <i class="os-icon os-icon-chevrons-left sliding-icon"></i>
      <span class="ml-1">Ver cotizaciones de {{ esFactura ? "factura" : "requerimiento" }}</span>
    </router-link>

    <!-- Encabezado -->
    <div class="element-box cr-encabezado">
      <div class="cr-encabezado-datos">
        <div class="cr-estado">
          <span class="badge" :class="claseEstado">{{ estadoTexto }}</span>
          <span v-if="esRequerimiento && current && current.esta_vencida" class="badge badge-danger ml-1">Vencida</span>
          <span v-if="esRequerimiento && current && current.requerimiento" class="badge badge-info ml-1">
            Req. #{{ current.requerimiento.numero }}
          </span>
          <button v-if="current" type="button" class="btn btn-sm btn-dark cr-btn-acciones"
            @click="panelAcciones = true">
            <i class="fa fa-cogs"></i>
            Acciones
          </button>
        </div>
        <div class="cr-titulo">
          Cotización {{ esFactura ? "factura" : "requerimiento" }}
          <span v-if="numero">#{{ numero }}</span>
        </div>
      </div>
      <div class="cr-total">
        <small>{{ lineas.length }} {{ lineas.length === 1 ? "producto" : "productos" }}</small>
        <div>Q {{ formatearMonto(total) }}</div>
      </div>
    </div>

    <!-- Pestanas (solo movil; en escritorio se ven ambas columnas) y herramientas de captura -->
    <div class="cr-barra-tabs">
      <ul class="nav nav-tabs d-md-none">
        <li class="nav-item">
          <a class="nav-link" :class="{ active: tabMovil === 'productos' }" href="#"
            @click.prevent="cambiarTab('productos')">
            Productos
            <span class="badge badge-pill badge-secondary">{{ lineas.length }}</span>
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{ active: tabMovil === 'cliente' }" href="#"
            @click.prevent="cambiarTab('cliente')">
            Cliente
          </a>
        </li>
      </ul>
      <div v-if="editable" class="cr-herramientas">
        <button type="button" class="btn btn-info" @click="abrirCamara" title="Escanear con la cámara">
          <i class="fas fa-camera"></i>
        </button>
        <button type="button" class="btn" :class="tecladoActivo ? 'btn-secondary' : 'btn-outline-secondary'"
          @click="alternarTeclado" title="Mostrar u ocultar el teclado en pantalla">
          <i class="fa fa-keyboard"></i>
        </button>
      </div>
    </div>

    <div class="row">
      <!-- PESTANA PRODUCTOS -->
      <div class="col-12 col-md-8" :class="{ 'd-none d-md-block': tabMovil !== 'productos' }">
        <div class="element-box cr-caja">
          <div v-if="!current" class="text-center text-muted py-4">Cargando cotización...</div>

          <template v-else>
            <div v-if="editable" class="cr-captura-wrapper">
              <label class="cr-label">Ingrese el sku o escanee la barra del producto</label>
              <div class="cr-captura">
                <input ref="inputCodigo" v-model="codigo" class="form-control cr-input-codigo"
                  :inputmode="tecladoActivo ? 'text' : 'none'" placeholder="sku o código de barras" type="text"
                  autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
                  @keyup.enter="capturarCodigo" />
                <button type="button" class="btn btn-primary" @click="capturarCodigo" :disabled="!codigo"
                  title="Agregar">
                  <i class="fa fa-plus"></i>
                </button>
              </div>
              <small v-if="procesando" class="form-text text-muted">Buscando {{ codigoEnProceso }}...</small>
              <small v-if="cola.length > 0" class="form-text text-info">Códigos en espera: {{ cola.length }}</small>
            </div>
            <div v-else class="alert alert-warning py-2">
              Esta cotización ya no admite productos ({{ estadoTexto }}).
            </div>

            <div v-if="lineas.length === 0" class="cr-vacio">
              <i class="fa fa-barcode"></i>
              <div>Aún no hay productos. Escanee el primero.</div>
            </div>

            <div v-for="linea in lineasOrdenadas" :key="linea.id" class="cr-linea"
              :class="{ 'cr-linea-reciente': linea.clave === ultimaClave }">
              <div class="cr-linea-numero">{{ linea.numero }}</div>
              <div class="cr-linea-info">
                <div>
                  <b>{{ linea.sku }}</b>
                  <i v-if="esRequerimiento && linea.es_inventariable === false"
                    class="fas fa-exclamation-triangle text-warning" title="No está marcado como inventariable"></i>
                </div>
                <div class="cr-linea-nombre">{{ linea.nombre }}</div>
                <small class="text-muted">{{ linea.unidad }} &middot; Q {{ formatearMonto(linea.precio) }}</small>
              </div>
              <div class="cr-linea-cantidad">
                <!-- Eliminar va en la misma fila que +/- para que queden alineados -->
                <div class="cr-linea-controles">
                  <div v-if="editable && esRequerimiento" class="cr-stepper">
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="cambiarCantidad(linea, -1)"
                      :disabled="Number(linea.cantidad_um) <= 1">
                      <i class="fa fa-minus"></i>
                    </button>
                    <span>{{ formatearCantidad(linea.cantidad_um) }}</span>
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="cambiarCantidad(linea, 1)">
                      <i class="fa fa-plus"></i>
                    </button>
                  </div>
                  <div v-else class="cr-cantidad">x {{ formatearCantidad(linea.cantidad_um) }}</div>
                  <button v-if="editable" type="button" class="btn btn-sm btn-outline-danger cr-btn-eliminar"
                    @click="eliminarLinea(linea)" title="Eliminar línea">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
                <div class="cr-linea-total">Q {{ formatearMonto(linea.total) }}</div>
              </div>
            </div>
          </template>
        </div>

        <div v-if="current" class="cr-acciones">
          <router-link class="btn btn-outline-info btn-block" :to="rutaDetalleCompleto">
            <i class="fa fa-external-link-alt"></i>
            Abrir en el módulo completo
          </router-link>
          <!-- Mientras la cotizacion esta abierta no se ofrece crear otra, para no dejarla a medias -->
          <router-link v-if="!editable" class="btn btn-outline-primary btn-block" :to="rutaNueva">
            <i class="fa fa-plus"></i>
            Nueva cotización
          </router-link>
        </div>
      </div>

      <!-- PESTANA CLIENTE -->
      <div class="col-12 col-md-4" :class="{ 'd-none d-md-block': tabMovil !== 'cliente' }">
        <div class="element-box cr-caja" v-if="current">
          <h6 class="cr-subtitulo">Cliente</h6>
          <dl class="cr-datos">
            <template v-if="datos.super_cliente">
              <dt>Supercliente</dt>
              <dd>{{ datos.super_cliente }}</dd>
            </template>
            <dt>NIT</dt>
            <dd>{{ datos.cliente_nit || "-" }}</dd>
            <dt>Nombre</dt>
            <dd>{{ datos.cliente_nombre || "-" }}</dd>
            <dt>Dirección</dt>
            <dd>{{ datos.cliente_direccion || "-" }}</dd>
            <template v-if="datos.email">
              <dt>Correo</dt>
              <dd>{{ datos.email }}</dd>
            </template>
          </dl>
          <button v-if="editable" type="button" class="btn btn-outline-primary btn-block mb-3"
            @click="abrirCambioCliente">
            <i class="fa fa-user-edit"></i>
            Cambiar cliente
          </button>
          <h6 class="cr-subtitulo">Cotización</h6>
          <dl class="cr-datos">
            <dt>Tipo</dt>
            <dd>{{ esFactura ? "Para factura" : "Para requerimiento" }}</dd>
            <dt>Tienda</dt>
            <dd>{{ datos.tienda || "-" }}</dd>
            <template v-if="esFactura">
              <dt>Caja</dt>
              <dd>{{ datos.caja || "-" }}</dd>
              <dt>Tipo de pago</dt>
              <dd>{{ datos.tipo_pago || "-" }}</dd>
            </template>
            <dt>Fecha</dt>
            <dd>{{ datos.fecha || "-" }}</dd>
            <template v-if="esRequerimiento">
              <dt>Vence</dt>
              <dd>{{ datos.fecha_vence || "-" }}</dd>
              <template v-if="current.requerimiento">
                <dt>Requerimiento</dt>
                <dd>#{{ current.requerimiento.numero }}</dd>
              </template>
            </template>
            <dt>Vendedor</dt>
            <dd>{{ datos.vendedor || "-" }}</dd>
          </dl>
        </div>
      </div>
    </div>

    <!-- Seleccion de unidad de medida del producto escaneado -->
    <div v-if="productoPendiente" class="cr-overlay cr-overlay-um">
      <div class="cr-panel">
        <div class="cr-panel-header">
          <b>{{ productoPendiente.sku }}</b>
          <div>{{ productoPendiente.nombre }}</div>
        </div>
        <div class="cr-panel-body">
          <p class="mb-2 text-muted">Seleccione la unidad de medida. Se agrega 1.</p>
          <button v-for="um in productoPendiente.unidades_medida" :key="um.id" type="button"
            class="btn btn-lg btn-block cr-btn-um"
            :class="um.id === productoPendiente.unidad_medida_sugerida ? 'btn-primary' : 'btn-outline-primary'"
            :disabled="agregando" @click="confirmarUnidad(um)">
            {{ um.nombre }}
            <small class="d-block">{{ um.cantidad_unidades }} {{ um.cantidad_unidades == 1 ? "unidad" : "unidades" }}
              <span v-if="um.id === productoPendiente.unidad_medida_sugerida"> &middot; según la barra</span></small>
          </button>
          <button type="button" class="btn btn-secondary btn-block mt-3" :disabled="agregando"
            @click="cancelarUnidad">
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Escaner con camara -->
    <div v-if="modalCamara" class="cr-overlay cr-overlay-camara">
      <div class="cr-camara">
        <div class="cr-camara-header">
          <span><i class="fas fa-camera mr-1"></i> Escanear con la cámara</span>
          <button type="button" class="close text-white" @click="cerrarCamara">
            <span>&times;</span>
          </button>
        </div>
        <div v-if="errorCamara" class="alert alert-danger m-3 small text-left">
          <b>{{ tituloErrorCamara }}</b>
          <div>{{ mensajeErrorCamara }}</div>
        </div>
        <div v-else class="cr-camara-visor">
          <EscanerCamaraBarras v-if="camaraActiva" @decode="onCamaraDecode" @loaded="onCamaraLoaded"
            @error="onCamaraError"></EscanerCamaraBarras>
          <div class="cr-camara-guia">
            <div class="cr-camara-recuadro" :class="{ 'cr-camara-flash': lecturaReciente }"></div>
          </div>
        </div>
        <div class="cr-camara-pie">
          <div class="small text-muted">Último código leído</div>
          <div class="cr-camara-codigo" :class="ultimoCodigoCamara ? 'text-success' : 'text-warning'">
            {{ ultimoCodigoCamara || "Esperando lectura..." }}
          </div>
          <button type="button" class="btn btn-secondary btn-block mt-2" @click="cerrarCamara">
            Cerrar cámara
          </button>
          <div v-if="motorCamara" class="small text-muted mt-1">Lector: {{ motorCamara }}</div>
        </div>
      </div>
    </div>

    <!-- Acciones de la cotizacion (las mismas del modulo completo) -->
    <div v-if="panelAcciones && current" class="cr-overlay cr-overlay-um" @click.self="panelAcciones = false">
      <div class="cr-panel">
        <div class="cr-panel-header">
          <b>Acciones</b>
          <div>Cotización {{ esFactura ? "factura" : "requerimiento" }} #{{ numero }}</div>
        </div>
        <div class="cr-panel-body">
          <template v-if="esRequerimiento">
            <button type="button" class="btn btn-outline-primary btn-block cr-btn-accion" :disabled="!puedeAbrir"
              @click="accionAbrir">
              <i class="fa fa-unlock"></i>
              Abrir cotización
              <small v-if="motivoNoAbrir" class="d-block">{{ motivoNoAbrir }}</small>
            </button>
            <button type="button" class="btn btn-outline-primary btn-block cr-btn-accion" @click="accionClonar">
              <i class="fa fa-copy"></i>
              Clonar cotización
              <small class="d-block">Crea una nueva abierta con precios del día</small>
            </button>
            <button type="button" class="btn btn-outline-primary btn-block cr-btn-accion" @click="accionTrasladar">
              <i class="fa fa-exchange-alt"></i>
              Trasladar a tienda
              <small class="d-block">Trae una cotización cerrada de otra tienda a esta tienda</small>
            </button>
          </template>
          <button v-if="esFactura" type="button" class="btn btn-outline-dark btn-block cr-btn-accion"
            :disabled="current.estado === 'A' || current.estado === '9'" @click="imprimir">
            <i class="fa fa-print"></i>
            Imprimir
            <small v-if="current.estado === 'A'" class="d-block">Primero genere la cotización</small>
          </button>
          <button type="button" class="btn btn-outline-danger btn-block cr-btn-accion" :disabled="!puedeAnular"
            @click="accionAnular">
            <i class="fa fa-archive"></i>
            Anular cotización
            <small v-if="motivoNoAnular" class="d-block">{{ motivoNoAnular }}</small>
          </button>
          <button type="button" class="btn btn-secondary btn-block mt-3" @click="panelAcciones = false">
            Cerrar
          </button>
        </div>
      </div>
    </div>

    <!-- Cambio de cliente (mismo buscador / cliente nuevo que al crear la cotizacion) -->
    <NuevaCotizacionMovilModal ref="modalCliente" @cliente="guardarCambioCliente" />

    <!-- Traslado de una cotizacion de otra tienda a la tienda de esta cotizacion -->
    <TrasladarCotizacionModal v-if="esRequerimiento && current" ref="modalTraslado" :cotizacion-id="id"
      :tienda-destino="current.tienda" @trasladada="onTrasladada" />

    <!-- Boton flotante al centro (como "Nuevo Conteo"): a la derecha tapaba el boton eliminar de las lineas -->
    <button v-if="current && editable" type="button" class="floated-chat-btn cr-btn-flotante" @click="finalizar"
      :disabled="lineas.length === 0 || finalizando">
      <i class="fas fa-clipboard-check"></i>&nbsp;{{ esFactura ? "Generar cotización" : "Cerrar cotización" }}
    </button>

    <!-- Aviso de resultado: verde agregado, rojo error del backend -->
    <transition name="cr-aviso">
      <div v-if="aviso" class="cr-aviso" :class="aviso.tipo === 'ok' ? 'cr-aviso-ok' : 'cr-aviso-error'"
        @click="cerrarAviso">
        <i :class="aviso.tipo === 'ok' ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
        <div class="cr-aviso-texto">
          <b>{{ aviso.titulo }}</b>
          <div>{{ aviso.mensaje }}</div>
          <button v-if="aviso.accion" type="button" class="btn btn-sm btn-light mt-2"
            @click.stop="irAccionAviso">
            {{ aviso.accion.texto }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Notifications from "@/notifications/notifications";
import EscanerCamaraBarras from "@/components/cat/rpt_etiquetas_custom/EscanerCamaraBarras";
import NuevaCotizacionMovilModal from "./NuevaCotizacionMovilModal";
import TrasladarCotizacionModal from "@/components/inventario/cotizaciones_requerimiento/TrasladarCotizacionModal";
import { mostrarResumenCargaCotizacion } from "@/components/inventario/cotizaciones_requerimiento/resumenCargaCotizacion";
import CotizacionMovilService, {
  TIPO_FACTURA,
  TIPO_REQUERIMIENTO,
} from "@/services/cxc/CotizacionMovilService";

const CLAVE_TECLADO = "cotizacion_movil_teclado";
const DURACION_AVISO_OK_MS = 1800;
const DURACION_AVISO_ERROR_MS = 4500;
// Aviso con boton de accion (ej. ir al listado): se deja mas tiempo para poder tocarlo
const DURACION_AVISO_ACCION_MS = 7000;
// Pausa para no repetir la lectura del mismo codigo frente a la camara
const COOLDOWN_CAMARA_MS = 1800;

/**
 * Pantalla de captura movil (handheld) de una cotizacion para factura
 * (pedido abierto) o para requerimiento (cotizacion tipo R).
 *
 * Cada codigo (SKU o barra) se resuelve en el backend, el usuario elige la
 * unidad de medida y se agrega 1 con el endpoint existente de cada tipo, que
 * hace todas las validaciones.
 */
export default {
  name: "CotizacionMovilEscaneoComponent",
  components: {
    EscanerCamaraBarras,
    NuevaCotizacionMovilModal,
    TrasladarCotizacionModal,
  },
  data() {
    return {
      current: null,
      tabMovil: "productos",
      codigo: "",
      codigoEnProceso: "",
      procesando: false,
      agregando: false,
      finalizando: false,
      cola: [],
      productoPendiente: null,
      ultimaClave: "",
      panelAcciones: false,
      // Aviso a mostrar despues de cambiar de cotizacion (ej. al clonar)
      avisoPendiente: null,
      tecladoActivo: this.leerPreferenciaTeclado(),
      aviso: null,
      // Camara
      modalCamara: false,
      camaraActiva: false,
      errorCamara: false,
      tituloErrorCamara: "",
      mensajeErrorCamara: "",
      motorCamara: "",
      ultimoCodigoCamara: "",
      lecturaReciente: false,
    };
  },
  computed: {
    tipo() {
      return this.$route.params.tipo;
    },
    id() {
      return this.$route.params.id;
    },
    esFactura() {
      return this.tipo === TIPO_FACTURA;
    },
    esRequerimiento() {
      return this.tipo === TIPO_REQUERIMIENTO;
    },
    numero() {
      return this.current ? this.current.numero : null;
    },
    total() {
      return this.current ? this.current.total : 0;
    },
    editable() {
      if (!this.current || this.current.estado !== "A") {
        return false;
      }
      if (this.esRequerimiento && this.current.requerimiento) {
        return this.current.requerimiento.estado === "A";
      }
      return true;
    },
    estadoTexto() {
      if (!this.current) {
        return "Cargando";
      }
      const textos = { A: "Abierta", B: "Cerrada", E: "Impresa", 9: "Anulada" };
      // Otros estados del pedido (ej. H = despachada) se toman del backend
      return textos[this.current.estado] || this.current.estado_str || this.current.estado;
    },
    claseEstado() {
      const estado = this.current ? this.current.estado : null;
      return {
        "badge-success": estado === "A",
        "badge-warning": !!estado && estado !== "A" && estado !== "9",
        "badge-danger": estado === "9",
        "badge-secondary": !estado,
      };
    },
    /**
     * Lineas normalizadas para ambos tipos.
     */
    lineas() {
      if (!this.current) {
        return [];
      }
      if (this.esFactura) {
        return (this.current.cxcpedidod_set || []).map((d) => {
          const producto = d.producto || {};
          const unidad = d.unidad_medida || {};
          return {
            id: d.id,
            clave: `${producto.sku}|${unidad.id}`,
            sku: producto.sku,
            nombre: producto.nombre,
            unidad: unidad.nombre,
            cantidad_um: d.cantidad_um,
            precio: d.precio,
            total: d.total,
            es_inventariable: true,
          };
        });
      }
      return (this.current.cxccotizaciond_set || []).map((d) => ({
        id: d.id,
        clave: `${d.producto}|${d.unidad_medida}`,
        sku: d.producto,
        nombre: d.producto_nombre,
        unidad: d.unidad_medida_nombre,
        cantidad_um: d.cantidad_um,
        precio: d.precio,
        total: d.total,
        es_inventariable: d.es_inventariable,
      }));
    },
    /**
     * El backend devuelve las lineas de la mas reciente a la mas antigua
     * (ordering -fecha_creacion): se numeran en orden de ingreso (1 = la primera)
     * y se muestran de la mas reciente a la mas antigua. La ultima escaneada va
     * siempre al inicio aunque haya sumado cantidad a una linea anterior; su
     * numero no cambia.
     */
    lineasOrdenadas() {
      const total = this.lineas.length;
      const lineas = this.lineas.map((linea, indice) =>
        Object.assign({}, linea, { numero: total - indice })
      );
      const indice = lineas.findIndex((linea) => linea.clave === this.ultimaClave);
      if (indice > 0) {
        lineas.unshift(lineas.splice(indice, 1)[0]);
      }
      return lineas;
    },
    rutaListado() {
      return { name: "pos-cotizacion-movil", query: { tipo: this.tipo } };
    },
    /**
     * Mismas reglas que el modulo completo de cotizaciones de requerimiento
     * (el backend vuelve a validarlas).
     */
    motivoNoAbrir() {
      const c = this.current;
      if (!c || this.esFactura) {
        return "";
      }
      if (c.estado !== "B") {
        return "Solo se abre una cotización cerrada";
      }
      if (c.esta_vencida) {
        return "Está vencida: clónela para reutilizarla";
      }
      if (c.requerimiento) {
        return `Asignada al requerimiento #${c.requerimiento.numero}`;
      }
      return "";
    },
    puedeAbrir() {
      return !!this.current && this.esRequerimiento && !this.motivoNoAbrir;
    },
    motivoNoAnular() {
      const c = this.current;
      if (!c) {
        return "";
      }
      if (c.estado === "9") {
        return "Ya está anulada";
      }
      if (this.esRequerimiento && c.requerimiento && c.requerimiento.estado !== "9") {
        return `Respalda el requerimiento #${c.requerimiento.numero}`;
      }
      return "";
    },
    puedeAnular() {
      return !!this.current && !this.motivoNoAnular;
    },
    rutaNueva() {
      return { name: "pos-cotizacion-movil", query: { tipo: this.tipo, nueva: "1" } };
    },
    datos() {
      const c = this.current || {};
      if (this.esFactura) {
        const cliente = c.cliente || {};
        return {
          cliente_nit: cliente.nit || c.cliente_nit,
          cliente_nombre: c.cliente_nombre || cliente.nombre,
          cliente_direccion: c.cliente_direccion || cliente.direccion,
          email: c.email || cliente.email,
          tienda: c.tienda ? c.tienda.nombre : "",
          caja: c.caja ? c.caja.caja : "",
          tipo_pago: c.tipo_pago,
          fecha: c.fecha,
          vendedor: c.vendedor ? c.vendedor.name || c.vendedor.username : "",
        };
      }
      return {
        super_cliente: c.super_cliente_obj ? c.super_cliente_obj.codigo : "",
        cliente_nit: c.cliente_nit,
        cliente_nombre: c.cliente_nombre,
        cliente_direccion: c.cliente_direccion,
        email: c.email,
        tienda: c.tienda_nombre,
        fecha: c.fecha,
        fecha_vence: c.fecha_vence,
        vendedor: c.vendedor_obj ? c.vendedor_obj.name || c.vendedor_obj.username : "",
      };
    },
    rutaDetalleCompleto() {
      if (this.esFactura) {
        return { name: "pos-cotizaciones-editar", params: { id: this.id } };
      }
      return { name: "inventario-cotizaciones-requerimiento-editar", params: { id: this.id } };
    },
  },
  watch: {
    "$route.params.id": function () {
      this.reiniciarPantalla();
      this.iniciar();
    },
  },
  mounted() {
    this.iniciar();
  },
  beforeDestroy() {
    this.cerrarCamara();
    this.limpiarTimers();
    if (this.audioCtx) {
      try {
        this.audioCtx.close();
      } catch (e) {
        // Sin soporte para cerrar el contexto de audio
      }
      this.audioCtx = null;
    }
  },
  methods: {
    iniciar() {
      if (!this.esFactura && !this.esRequerimiento) {
        this.$router.replace({ name: "pos-cotizacion-movil" });
        return;
      }
      this.cargar(true).then(() => {
        this.enfocarInput();
        if (this.avisoPendiente) {
          const pendiente = this.avisoPendiente;
          this.avisoPendiente = null;
          this.mostrarAviso(pendiente.tipo, pendiente.titulo, pendiente.mensaje);
        }
      });
    },
    reiniciarPantalla() {
      this.cerrarCamara();
      this.limpiarTimers();
      this.current = null;
      this.codigo = "";
      this.cola = [];
      this.productoPendiente = null;
      this.ultimaClave = "";
      this.aviso = null;
      this.panelAcciones = false;
      this.tabMovil = "productos";
    },
    limpiarTimers() {
      if (this.timerAviso) {
        clearTimeout(this.timerAviso);
        this.timerAviso = null;
      }
      if (this.timerCooldown) {
        clearTimeout(this.timerCooldown);
        this.timerCooldown = null;
      }
    },
    cargar(loading) {
      const id = this.id;
      return CotizacionMovilService.obtener(this.tipo, id, loading)
        .then((response) => {
          if (this.id === id) {
            this.current = response.data;
          }
        })
        .catch((error) => {
          this.mostrarAviso("error", "No se pudo cargar la cotización", this.mensajeError(error));
        });
    },
    cambiarTab(tab) {
      this.tabMovil = tab;
      if (tab === "productos") {
        this.enfocarInput();
      }
    },
    enfocarInput() {
      this.$nextTick(() => {
        if (this.$refs.inputCodigo && !this.productoPendiente && !this.modalCamara) {
          this.$refs.inputCodigo.focus();
        }
      });
    },
    leerPreferenciaTeclado() {
      try {
        return localStorage.getItem(CLAVE_TECLADO) !== "0";
      } catch (e) {
        return true;
      }
    },
    alternarTeclado() {
      this.tabMovil = "productos";
      this.tecladoActivo = !this.tecladoActivo;
      try {
        localStorage.setItem(CLAVE_TECLADO, this.tecladoActivo ? "1" : "0");
      } catch (e) {
        // Almacenamiento no disponible: solo aplica en esta sesion
      }
      if (this.$refs.inputCodigo) {
        this.$refs.inputCodigo.blur();
      }
      this.enfocarInput();
    },

    // ------------------------------------------------------------------
    // Captura de codigos
    // ------------------------------------------------------------------
    capturarCodigo() {
      const codigo = (this.codigo || "").trim();
      // Se limpia de inmediato para que el lector pueda seguir escaneando
      this.codigo = "";
      if (codigo) {
        this.procesarCodigo(codigo);
      }
    },
    procesarCodigo(codigo) {
      if (!this.editable) {
        this.mostrarAviso("error", "No se puede agregar", "La cotización ya no admite productos.");
        return;
      }
      if (this.procesando || this.agregando || this.productoPendiente) {
        this.cola.push(codigo);
        return;
      }
      this.procesando = true;
      this.codigoEnProceso = codigo;
      CotizacionMovilService.obtenerCodigo(codigo)
        .then((response) => {
          this.procesando = false;
          this.codigoEnProceso = "";
          const producto = response.data;
          if (!producto.unidades_medida || producto.unidades_medida.length === 0) {
            this.mostrarAviso("error", `SKU ${producto.sku}`, "El producto no tiene unidades de medida activas.");
            this.siguienteEnCola();
            return;
          }
          this.productoPendiente = Object.assign({ codigo: codigo }, producto);
          if (this.$refs.inputCodigo) {
            this.$refs.inputCodigo.blur();
          }
        })
        .catch((error) => {
          this.procesando = false;
          this.codigoEnProceso = "";
          this.mostrarAviso("error", `Código ${codigo}`, this.mensajeError(error));
          this.siguienteEnCola();
        });
    },
    siguienteEnCola() {
      if (this.cola.length > 0 && !this.procesando && !this.agregando && !this.productoPendiente) {
        this.procesarCodigo(this.cola.shift());
      }
    },
    confirmarUnidad(unidad) {
      if (this.agregando || !this.productoPendiente) {
        return;
      }
      const producto = this.productoPendiente;
      this.agregando = true;
      CotizacionMovilService.agregar(this.tipo, this.id, producto.sku, unidad.id, 1)
        .then((response) => {
          this.ultimaClave = `${producto.sku}|${unidad.id}`;
          this.mostrarAviso("ok", "Producto agregado", `${producto.sku} ${producto.nombre} - 1 ${unidad.nombre}`);
          const advertencias = (response && response.data && response.data.advertencias) || [];
          advertencias.forEach((mensaje) => Notifications.toast.warning("Advertencia", mensaje));
          return this.cargar(false);
        })
        .catch((error) => {
          this.mostrarAviso("error", `No se agregó ${producto.sku}`, this.mensajeError(error));
          // El encabezado pudo cambiar (cerrado, anulado, caja cerrada) desde otro equipo
          return this.cargar(false);
        })
        .finally(() => {
          this.agregando = false;
          this.cerrarSeleccionUnidad();
        });
    },
    cancelarUnidad() {
      if (this.agregando) {
        return;
      }
      this.cerrarSeleccionUnidad();
    },
    cerrarSeleccionUnidad() {
      this.productoPendiente = null;
      if (this.modalCamara) {
        // Evita que el mismo producto, aun frente a la camara, se lea otra vez de inmediato
        this.iniciarCooldownCamara();
      }
      this.enfocarInput();
      this.siguienteEnCola();
    },

    // ------------------------------------------------------------------
    // Lineas
    // ------------------------------------------------------------------
    cambiarCantidad(linea, delta) {
      const nueva = Number(linea.cantidad_um) + delta;
      if (nueva <= 0) {
        return;
      }
      CotizacionMovilService.modificarCantidad(this.id, linea.id, nueva)
        .then(() => {
          this.ultimaClave = linea.clave;
          return this.cargar(false);
        })
        .catch((error) => {
          this.mostrarAviso("error", `No se modificó ${linea.sku}`, this.mensajeError(error));
          return this.cargar(false);
        });
    },
    eliminarLinea(linea) {
      Notifications.alert.confirm(
        "Eliminar producto",
        `¿Eliminar ${linea.sku} ${linea.nombre} (${linea.unidad})?`,
        "Sí, eliminar",
        () => {
          CotizacionMovilService.eliminarLinea(this.tipo, this.id, linea.id)
            .then(() => {
              this.mostrarAviso("ok", "Producto eliminado", `${linea.sku} ${linea.nombre}`);
              return this.cargar(false);
            })
            .catch((error) => {
              this.mostrarAviso("error", `No se eliminó ${linea.sku}`, this.mensajeError(error));
            })
            .finally(() => this.enfocarInput());
        }
      );
    },
    finalizar() {
      const titulo = this.esFactura ? "Generar cotización" : "Cerrar cotización";
      let pregunta = "Ya no podrá agregar productos. ¿Desea continuar?";
      if (this.esRequerimiento && this.current.requerimiento) {
        pregunta = `Sus productos se agregarán al requerimiento #${this.current.requerimiento.numero} y ya no podrá modificarse. ¿Desea continuar?`;
      } else if (this.esRequerimiento) {
        pregunta = "Una vez cerrada podrá asignarse a un requerimiento y ya no podrá agregar productos. ¿Desea continuar?";
      }
      Notifications.alert.confirm(titulo, pregunta, "Sí, continuar", () => {
        this.finalizando = true;
        CotizacionMovilService.finalizar(this.tipo, this.id)
          .then((response) => {
            const carga = response && response.data ? response.data.carga_requerimiento : null;
            const fallidos = carga && carga.fallidos ? carga.fallidos : [];
            if (this.esRequerimiento && fallidos.length > 0) {
              // El detalle de productos que no se cargaron al requerimiento se deja en el modal: hay que leerlo
              mostrarResumenCargaCotizacion(carga);
            } else {
              this.mostrarAviso("ok", titulo, this.mensajeFinalizada(carga), {
                texto: `Ir a cotizaciones de ${this.esFactura ? "factura" : "requerimiento"}`,
                ruta: this.rutaListado,
              });
            }
            return this.cargar(true);
          })
          .catch((error) => {
            this.mostrarAviso("error", titulo, this.mensajeError(error), {
              texto: `Ir a cotizaciones de ${this.esFactura ? "factura" : "requerimiento"}`,
              ruta: this.rutaListado,
            });
          })
          .finally(() => {
            this.finalizando = false;
          });
      });
    },
    abrirCambioCliente() {
      // Requerimiento: el supercliente debe ser de la tienda de la cotizacion
      const tienda = !this.esFactura && this.current ? this.current.tienda : null;
      this.$refs.modalCliente.abrirCambioCliente(this.tipo, tienda);
    },

    // ------------------------------------------------------------------
    // Acciones (abrir, clonar, anular)
    // ------------------------------------------------------------------
    accionAbrir() {
      this.panelAcciones = false;
      Notifications.alert.confirm("Abrir cotización", "¿Desea abrir la cotización para editarla?", "Sí, abrir", () => {
        CotizacionMovilService.abrir(this.id)
          .then(() => {
            this.mostrarAviso("ok", "Cotización abierta", "Puede seguir agregando productos.");
            return this.cargar(true).then(() => this.enfocarInput());
          })
          .catch((error) => this.mostrarAviso("error", "Abrir cotización", this.mensajeError(error)));
      });
    },
    accionAnular() {
      this.panelAcciones = false;
      Notifications.alert.confirm("Anular cotización", "¿Desea anular la cotización?", "Sí, anular", () => {
        CotizacionMovilService.anular(this.tipo, this.id)
          .then(() => {
            this.mostrarAviso("ok", "Cotización anulada", `Cotización #${this.numero}`, {
              texto: `Ir a cotizaciones de ${this.esFactura ? "factura" : "requerimiento"}`,
              ruta: this.rutaListado,
            });
            return this.cargar(true);
          })
          .catch((error) => this.mostrarAviso("error", "Anular cotización", this.mensajeError(error)));
      });
    },
    accionClonar() {
      this.panelAcciones = false;
      Notifications.alert.confirm(
        "Clonar cotización",
        `¿Está seguro de duplicar la cotización #${this.numero}?`,
        "Sí, duplicar",
        () => {
          CotizacionMovilService.clonar(this.id)
            .then((response) => {
              const data = response.data || {};
              (data.advertencias || []).forEach((mensaje) => Notifications.toast.warning("Advertencia", mensaje));
              // La pantalla se recarga con la cotizacion nueva (watch de $route.params.id)
              // y al terminar de cargar muestra el aviso
              this.avisoPendiente = {
                tipo: "ok",
                titulo: "Cotización clonada",
                mensaje: `Se creó la cotización #${data.numero} a partir de la #${this.numero}`,
              };
              this.$router.push({
                name: "pos-cotizacion-movil-escaneo",
                params: { tipo: TIPO_REQUERIMIENTO, id: data.cotizacion_e },
              });
            })
            .catch((error) => this.mostrarAviso("error", "Clonar cotización", this.mensajeError(error)));
        }
      );
    },
    accionTrasladar() {
      this.panelAcciones = false;
      this.$refs.modalTraslado.abrir();
    },
    onTrasladada(data) {
      (data.advertencias || []).forEach((mensaje) => Notifications.toast.warning("Advertencia", mensaje));
      // Igual que al clonar: se abre la cotizacion nueva y al terminar de cargar se muestra el aviso
      this.avisoPendiente = {
        tipo: "ok",
        titulo: "Cotización trasladada",
        mensaje: `Se creó la cotización #${data.numero} a partir de la #${data.numero_origen} de la tienda ${data.tienda_origen}, que quedó anulada.`,
      };
      this.$router.push({
        name: "pos-cotizacion-movil-escaneo",
        params: { tipo: TIPO_REQUERIMIENTO, id: data.cotizacion_e },
      });
    },
    guardarCambioCliente(cliente) {
      CotizacionMovilService.cambiarCliente(this.tipo, this.current, cliente)
        .then(() => {
          this.$refs.modalCliente.terminarCambioCliente(null);
          this.mostrarAviso("ok", "Cliente actualizado", `${cliente.nit} - ${cliente.nombre}`);
          return this.cargar(false);
        })
        .catch((error) => {
          this.$refs.modalCliente.terminarCambioCliente(error);
        });
    },
    mensajeFinalizada(carga) {
      const numero = this.numero ? ` #${this.numero}` : "";
      if (this.esFactura) {
        return `Cotización${numero} generada con éxito.`;
      }
      if (carga) {
        const agregados = (carga.agregados || []).length;
        return `Cotización${numero} cerrada. ${agregados} producto(s) agregados al requerimiento #${carga.requerimiento}.`;
      }
      return `Cotización${numero} cerrada con éxito.`;
    },
    imprimir() {
      const id = String(this.id).replace(/-/g, "");
      const baseApiUrl = process.env.VUE_APP_ROOT_API;
      const usuario = localStorage.getItem("id_usuario");
      window.open(
        `${baseApiUrl}/cat/reporte/?report_format=pdf&report_name=cxc/pedidos/Pedido.jrxml&usuario=${usuario}&id=${id}`
      );
    },

    // ------------------------------------------------------------------
    // Camara
    // ------------------------------------------------------------------
    abrirCamara() {
      // Los botones de camara y teclado estan junto a las pestanas: se vuelve a Productos
      this.tabMovil = "productos";
      this.errorCamara = false;
      this.tituloErrorCamara = "";
      this.mensajeErrorCamara = "";
      this.ultimoCodigoCamara = "";
      const seguro =
        window.isSecureContext ||
        window.location.protocol === "https:" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.errorCamara = true;
        this.tituloErrorCamara = "Cámara no soportada";
        this.mensajeErrorCamara = "El navegador o dispositivo no permite usar la cámara.";
      } else if (!seguro) {
        this.errorCamara = true;
        this.tituloErrorCamara = "Conexión no segura (HTTP)";
        this.mensajeErrorCamara =
          "La cámara solo funciona ingresando por HTTPS. Contacte al administrador para verificar la conexión.";
      }
      if (this.$refs.inputCodigo) {
        this.$refs.inputCodigo.blur();
      }
      this.modalCamara = true;
      this.$nextTick(() => {
        this.camaraActiva = !this.errorCamara;
      });
    },
    cerrarCamara() {
      this.camaraActiva = false;
      this.modalCamara = false;
      if (this.timerCooldown) {
        clearTimeout(this.timerCooldown);
        this.timerCooldown = null;
      }
      this.enfocarInput();
    },
    onCamaraLoaded(info) {
      this.motorCamara = info && info.motor ? info.motor : "";
    },
    onCamaraError(mensaje) {
      this.camaraActiva = false;
      this.errorCamara = true;
      this.tituloErrorCamara = "No se pudo usar la cámara";
      this.mensajeErrorCamara = mensaje;
    },
    onCamaraDecode(resultado) {
      const codigo = String(resultado || "").trim();
      if (!codigo) {
        return;
      }
      // Mientras se elige la unidad de medida no se toman nuevas lecturas
      if (this.productoPendiente || this.agregando || this.procesando) {
        return;
      }
      if (this.ultimoCodigoCamara === codigo && this.timerCooldown) {
        return;
      }
      this.ultimoCodigoCamara = codigo;
      this.lecturaReciente = true;
      setTimeout(() => {
        this.lecturaReciente = false;
      }, 350);
      this.sonido(true);
      this.iniciarCooldownCamara();
      this.procesarCodigo(codigo);
    },
    iniciarCooldownCamara() {
      if (this.timerCooldown) {
        clearTimeout(this.timerCooldown);
      }
      this.timerCooldown = setTimeout(() => {
        this.timerCooldown = null;
      }, COOLDOWN_CAMARA_MS);
    },

    // ------------------------------------------------------------------
    // Avisos
    // ------------------------------------------------------------------
    /**
     * accion (opcional): { texto, ruta } muestra un boton dentro del aviso.
     */
    mostrarAviso(tipo, titulo, mensaje, accion) {
      if (this.timerAviso) {
        clearTimeout(this.timerAviso);
      }
      this.aviso = { tipo: tipo, titulo: titulo, mensaje: mensaje, accion: accion || null };
      if (tipo !== "ok") {
        this.sonido(false);
      }
      let duracion = tipo === "ok" ? DURACION_AVISO_OK_MS : DURACION_AVISO_ERROR_MS;
      if (accion) {
        duracion = DURACION_AVISO_ACCION_MS;
      }
      this.timerAviso = setTimeout(() => {
        this.aviso = null;
        this.timerAviso = null;
      }, duracion);
    },
    irAccionAviso() {
      const accion = this.aviso ? this.aviso.accion : null;
      this.cerrarAviso();
      if (accion && accion.ruta) {
        this.$router.push(accion.ruta);
      }
    },
    cerrarAviso() {
      if (this.timerAviso) {
        clearTimeout(this.timerAviso);
        this.timerAviso = null;
      }
      this.aviso = null;
    },
    /**
     * Tono corto agudo para lectura correcta; tono grave y vibracion larga para error.
     */
    sonido(correcto) {
      if (navigator.vibrate) {
        try {
          navigator.vibrate(correcto ? 60 : [120, 60, 120]);
        } catch (e) {
          // Vibracion no soportada
        }
      }
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) {
          return;
        }
        // Un solo AudioContext reutilizado: crear uno por lectura agota el limite del navegador
        if (!this.audioCtx) {
          this.audioCtx = new AudioCtx();
        }
        const ctx = this.audioCtx;
        if (ctx.state === "suspended") {
          ctx.resume();
        }
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = correcto ? "sine" : "square";
        osc.frequency.setValueAtTime(correcto ? 1750 : 320, ctx.currentTime);
        gain.gain.setValueAtTime(correcto ? 0.25 : 0.2, ctx.currentTime);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + (correcto ? 0.12 : 0.35));
      } catch (e) {
        // Restricciones de audio del navegador
      }
    },
    /**
     * Texto legible del error devuelto por el backend.
     */
    mensajeError(error) {
      if (!error || !error.response) {
        return "Sin respuesta del servidor. Verifique la conexión.";
      }
      const data = error.response.data;
      if (!data) {
        return `Error ${error.response.status}`;
      }
      if (typeof data === "string") {
        // Una pagina HTML de error no se muestra completa
        return data.length > 200 ? `Error ${error.response.status} en el servidor` : data;
      }
      if (Array.isArray(data)) {
        return data.join(" ");
      }
      if (data.detail) {
        return Array.isArray(data.detail) ? data.detail.join(" ") : String(data.detail);
      }
      return Object.keys(data)
        .map((campo) => {
          const valor = data[campo];
          return `${campo}: ${Array.isArray(valor) ? valor.join(" ") : valor}`;
        })
        .join(" | ");
    },
    formatearMonto(valor) {
      const numero = Number(valor || 0);
      return numero.toLocaleString("es-GT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatearCantidad(valor) {
      const numero = Number(valor || 0);
      return Number.isInteger(numero) ? String(numero) : numero.toFixed(2);
    },
  },
};
</script>

<style scoped>
.cr-pantalla {
  /* Espacio para que el boton flotante no tape la ultima linea */
  padding-bottom: 96px;
}

.cr-barra-tabs {
  display: flex;
  align-items: flex-end;
  margin-bottom: 8px;
}

.cr-barra-tabs .nav-tabs {
  flex: 1;
}

.cr-herramientas {
  display: flex;
  margin-left: auto;
  padding-bottom: 4px;
}

/* Margenes en lugar de gap: algunas handheld usan WebView antiguos */
.cr-herramientas .btn {
  min-width: 46px;
  min-height: 40px;
  margin-left: 6px;
}

.cr-btn-flotante {
  /* Centrado sin transform: la animacion jumpUp del tema usa transform */
  left: 0;
  right: 0;
  bottom: 14px;
  /* El tema usa 9999: quedaria encima de la camara y de la seleccion de unidad (1060+) */
  z-index: 1040;
  margin: 0 auto;
  width: max-content;
  max-width: calc(100% - 24px);
  border: 0;
  font-family: inherit;
  font-weight: bold;
  justify-content: center;
}

.cr-btn-flotante:disabled {
  opacity: 0.55;
  box-shadow: none;
  cursor: not-allowed;
}

.cr-btn-listado {
  margin-bottom: 10px;
  min-height: 36px;
}

.cr-encabezado {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin-bottom: 12px;
}

.cr-titulo {
  font-weight: bold;
  font-size: 1.05rem;
}

.cr-estado {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 2px;
}

.cr-btn-acciones {
  margin-left: 8px;
  padding: 2px 10px;
}

.cr-btn-accion {
  min-height: 54px;
  text-align: left;
  font-weight: bold;
}

.cr-btn-accion i {
  width: 22px;
}

.cr-btn-accion small {
  font-weight: normal;
  padding-left: 26px;
}

/* Numero de linea en orden de ingreso */
.cr-linea-numero {
  flex: 0 0 auto;
  min-width: 26px;
  height: 26px;
  line-height: 26px;
  padding: 0 4px;
  margin-right: 8px;
  border-radius: 13px;
  background-color: #e9ecef;
  color: #495057;
  font-weight: bold;
  font-size: 0.8rem;
  text-align: center;
  align-self: flex-start;
}

.cr-linea-reciente .cr-linea-numero {
  background-color: #28a745;
  color: #fff;
}

.cr-total {
  text-align: right;
  color: #24b314;
  font-weight: bold;
  font-size: 1.35rem;
  line-height: 1.1;
}

.cr-total small {
  display: block;
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: normal;
}

.cr-caja {
  padding: 12px 16px;
  margin-bottom: 12px;
}

.cr-label {
  font-weight: bold;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.cr-captura-wrapper {
  margin-bottom: 12px;
}

/* Margenes en lugar de gap: algunas handheld usan WebView antiguos */
.cr-captura {
  display: flex;
  align-items: stretch;
}

.cr-captura .btn {
  min-width: 48px;
  margin-left: 6px;
}

.cr-input-codigo {
  flex: 1;
  min-width: 0;
  min-height: 52px;
  font-size: 1.25rem;
  font-weight: bold;
}

.cr-vacio {
  text-align: center;
  color: #9aa0a6;
  padding: 24px 0;
}

.cr-vacio .fa {
  font-size: 2.5rem;
  margin-bottom: 8px;
}

.cr-linea {
  display: flex;
  align-items: center;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  transition: background-color 0.4s ease;
}

.cr-linea-reciente {
  border-color: #28a745;
  background-color: #eafaf0;
}

.cr-linea-info {
  flex: 1;
  min-width: 0;
}

.cr-linea-nombre {
  font-size: 0.9rem;
  overflow-wrap: anywhere;
}

.cr-linea-cantidad {
  text-align: right;
  margin-left: 8px;
  white-space: nowrap;
}

.cr-cantidad {
  font-weight: bold;
  font-size: 1.05rem;
}

.cr-stepper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.cr-stepper span {
  display: inline-block;
  min-width: 34px;
  text-align: center;
  font-weight: bold;
  font-size: 1.05rem;
}

.cr-stepper .btn {
  min-width: 36px;
  min-height: 36px;
}

.cr-linea-total {
  font-weight: bold;
  color: #1a73e8;
}

.cr-linea-controles {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* Mismo alto que los botones +/- para que queden en linea */
.cr-btn-eliminar {
  margin-left: 8px;
  min-width: 36px;
  min-height: 36px;
}

.cr-acciones .btn {
  min-height: 48px;
  margin-bottom: 8px;
}

.cr-subtitulo {
  font-weight: bold;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 6px;
  margin-bottom: 8px;
}

.cr-datos dt {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: normal;
  margin-bottom: 0;
}

.cr-datos dd {
  font-weight: bold;
  margin-bottom: 8px;
  overflow-wrap: anywhere;
}

/* Capas superpuestas */
.cr-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  overflow-y: auto;
}

.cr-overlay-camara {
  z-index: 1060;
  background-color: rgba(0, 0, 0, 0.88);
}

.cr-overlay-um {
  z-index: 1080;
  background-color: rgba(0, 0, 0, 0.6);
}

.cr-panel {
  background-color: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 460px;
  max-height: 100%;
  overflow-y: auto;
}

.cr-panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.cr-panel-body {
  padding: 12px 16px 16px;
}

.cr-btn-um {
  min-height: 60px;
  font-weight: bold;
}

.cr-camara {
  background-color: #1a1a24;
  color: #fff;
  border-radius: 10px;
  width: 100%;
  max-width: 520px;
  overflow: hidden;
}

.cr-camara-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background-color: #000;
}

.cr-camara-visor {
  position: relative;
  min-height: 290px;
  background-color: #000;
}

.cr-camara-guia {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.cr-camara-recuadro {
  width: 75%;
  height: 40%;
  border: 3px solid #17a2b8;
  border-radius: 8px;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.cr-camara-flash {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.25);
}

.cr-camara-pie {
  padding: 10px 14px 14px;
  text-align: center;
}

.cr-camara-codigo {
  font-weight: bold;
  font-size: 1.05rem;
}

/* Aviso de resultado */
.cr-aviso {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  width: calc(100% - 24px);
  max-width: 520px;
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 8px;
  color: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.cr-aviso i {
  font-size: 2rem;
  margin-right: 12px;
}

.cr-aviso-texto {
  flex: 1;
  min-width: 0;
}

.cr-aviso-ok {
  background-color: #28a745;
}

.cr-aviso-error {
  background-color: #dc3545;
}

.cr-aviso-enter-active,
.cr-aviso-leave-active {
  transition: opacity 0.2s ease;
}

.cr-aviso-enter,
.cr-aviso-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .cr-pantalla {
    /* Conserva el espacio inferior del boton flotante tambien en movil */
    padding: 8px 8px 96px !important;
  }

  .cr-encabezado {
    padding: 10px 12px;
  }

  /* La linea de las pestanas continua bajo los botones de camara y teclado */
  .cr-herramientas {
    border-bottom: 1px solid #dee2e6;
  }

  .cr-caja {
    padding: 10px;
  }

  .cr-camara {
    border-radius: 0;
  }

  .cr-overlay-camara {
    padding: 0;
  }
}
</style>
